import { useEffect, useMemo, useState } from "react";
import { api, type MailAttachment, type MailCalendarInvite, type MailMessage } from "@/lib/api";
import {
  isCalendarAttachment,
  parseIcs,
  rsvpLabel,
  type CalendarInvite,
  type RsvpStatus,
} from "@/mail/ics";

type Props = {
  message: MailMessage;
  accountId: string;
  accountEmail: string;
  /** Load .ics bytes when the API did not already parse calendarInvite. */
  fetchAttachmentText?: (file: MailAttachment) => Promise<string | null>;
};

type RsvpLogEntry = {
  at: string;
  responseStatus: RsvpStatus;
  comment: string;
};

const HUB_DOWNLOAD_URL = "https://atw.net/hub";

function formatWhen(invite: CalendarInvite | MailCalendarInvite): string {
  const start = invite.start || "";
  const end = invite.end || "";
  if (!start && !end) return "";
  try {
    const startDate = start ? new Date(start) : null;
    const endDate = end ? new Date(end) : null;
    if (startDate && !Number.isNaN(startDate.getTime())) {
      const day = startDate.toLocaleString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
      const startTime = startDate.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
      if (endDate && !Number.isNaN(endDate.getTime())) {
        const endTime = endDate.toLocaleTimeString(undefined, {
          hour: "numeric",
          minute: "2-digit",
        });
        return `${day} · ${startTime} – ${endTime}`;
      }
      return `${day} · ${startTime}`;
    }
  } catch {
    /* fall through */
  }
  return [start, end].filter(Boolean).join(" – ");
}

function pickCalendarFile(message: MailMessage): MailAttachment | null {
  const files = Array.isArray(message.attachments) ? message.attachments : [];
  return files.find((file) => isCalendarAttachment(file)) || null;
}

function rsvpLogKey(accountId: string, uid: string): string {
  return `atw-hub-rsvp-log:${accountId}:${uid}`;
}

function readRsvpLog(accountId: string, uid: string): RsvpLogEntry[] {
  if (typeof localStorage === "undefined" || !accountId || !uid) return [];
  try {
    const raw = localStorage.getItem(rsvpLogKey(accountId, uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((row) => {
        if (!row || typeof row !== "object") return null;
        const entry = row as Record<string, unknown>;
        const responseStatus = String(entry.responseStatus || "");
        if (!["accepted", "declined", "tentative"].includes(responseStatus)) return null;
        return {
          at: typeof entry.at === "string" ? entry.at : "",
          responseStatus: responseStatus as RsvpStatus,
          comment: typeof entry.comment === "string" ? entry.comment : "",
        };
      })
      .filter((row): row is RsvpLogEntry => Boolean(row?.at));
  } catch {
    return [];
  }
}

function writeRsvpLog(accountId: string, uid: string, entries: RsvpLogEntry[]) {
  if (typeof localStorage === "undefined" || !accountId || !uid) return;
  try {
    localStorage.setItem(rsvpLogKey(accountId, uid), JSON.stringify(entries.slice(0, 40)));
  } catch {
    /* ignore quota */
  }
}

function formatLogWhen(iso: string): string {
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function MailInviteRsvpCard({
  message,
  accountId,
  accountEmail,
  fetchAttachmentText,
}: Props) {
  const seeded = message.calendarInvite || null;
  const [invite, setInvite] = useState<CalendarInvite | MailCalendarInvite | null>(seeded);
  const [loadingInvite, setLoadingInvite] = useState(!seeded && Boolean(pickCalendarFile(message)));
  const [status, setStatus] = useState<RsvpStatus | "">(
    (seeded?.responseStatus as RsvpStatus) || "",
  );
  const [comment, setComment] = useState("");
  const [pending, setPending] = useState<RsvpStatus | "">("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState<RsvpLogEntry[]>([]);

  useEffect(() => {
    setInvite(message.calendarInvite || null);
    setStatus((message.calendarInvite?.responseStatus as RsvpStatus) || "");
  }, [message.id, message.calendarInvite]);

  useEffect(() => {
    const uid = invite?.uid || message.calendarInvite?.uid || "";
    if (!uid) {
      setLog([]);
      return;
    }
    const entries = readRsvpLog(accountId, uid);
    setLog(entries);
    if (!status && entries[0]?.responseStatus) {
      setStatus(entries[0].responseStatus);
    }
  }, [accountId, invite?.uid, message.calendarInvite?.uid]);

  useEffect(() => {
    if (message.calendarInvite || !fetchAttachmentText) {
      setLoadingInvite(false);
      return;
    }
    const file = pickCalendarFile(message);
    if (!file) {
      setLoadingInvite(false);
      return;
    }
    let cancelled = false;
    setLoadingInvite(true);
    void (async () => {
      try {
        const text = await fetchAttachmentText(file);
        if (cancelled) return;
        const parsed = text ? parseIcs(text) : null;
        if (parsed) setInvite(parsed);
      } catch {
        /* card stays hidden when parse fails */
      } finally {
        if (!cancelled) setLoadingInvite(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [message, fetchAttachmentText]);

  const organizerIsSelf =
    Boolean(invite?.organizerEmail) &&
    invite!.organizerEmail!.toLowerCase() === accountEmail.trim().toLowerCase();

  const logNewestFirst = useMemo(
    () => [...log].sort((a, b) => b.at.localeCompare(a.at)),
    [log],
  );

  if (loadingInvite) {
    return (
      <div className="mail-rsvp-card is-loading" role="status">
        Loading meeting invite…
      </div>
    );
  }
  if (!invite?.uid || organizerIsSelf) return null;

  const title = invite.summary || message.subject || "Meeting invite";
  const when = formatWhen(invite);
  const organizer =
    invite.organizerName || invite.organizerEmail
      ? [invite.organizerName, invite.organizerEmail].filter(Boolean).join(" · ")
      : "";

  async function respond(next: RsvpStatus) {
    if (busy) return;
    setBusy(true);
    setPending(next);
    setError("");
    const note = comment.trim();
    try {
      const result = await api<{
        ok?: boolean;
        responseStatus?: string;
        message?: string;
        calendarWarning?: string;
      }>("/api/mail/calendar-rsvp", {
        method: "POST",
        body: JSON.stringify({
          accountId,
          responseStatus: next,
          comment: note,
          invite: {
            uid: invite!.uid,
            summary: invite!.summary,
            start: invite!.start,
            end: invite!.end,
            organizerEmail: invite!.organizerEmail,
            sequence: invite!.sequence,
          },
        }),
      });
      const savedStatus = (result.responseStatus as RsvpStatus) || next;
      setStatus(savedStatus);
      const entry: RsvpLogEntry = {
        at: new Date().toISOString(),
        responseStatus: savedStatus,
        comment: note,
      };
      const nextLog = [entry, ...readRsvpLog(accountId, invite!.uid)];
      writeRsvpLog(accountId, invite!.uid, nextLog);
      setLog(nextLog);
      setComment("");
      if (result.calendarWarning) setError(result.calendarWarning);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send RSVP.");
    } finally {
      setBusy(false);
      setPending("");
    }
  }

  return (
    <div className="mail-rsvp-card" role="region" aria-label="Meeting response">
      <div className="mail-rsvp-heading">
        <strong>{title}</strong>
        {when ? <p className="mail-rsvp-when muted">{when}</p> : null}
        {organizer ? <p className="mail-rsvp-organizer muted">From {organizer}</p> : null}
        {status ? (
          <p className="mail-rsvp-status">
            Your response: <strong>{rsvpLabel(status)}</strong>
          </p>
        ) : null}
      </div>

      <div className="mail-rsvp-message-wrap">
        <label className="mail-rsvp-message-label" htmlFor={`mail-rsvp-note-${message.id}`}>
          Optional message
        </label>
        <textarea
          id={`mail-rsvp-note-${message.id}`}
          className="mail-rsvp-textarea"
          rows={3}
          value={comment}
          disabled={busy}
          placeholder="Add a note for the organizer (optional)"
          onChange={(event) => setComment(event.target.value)}
        />
      </div>

      <div className="mail-rsvp-actions" role="group" aria-label="Respond to invite">
        <button
          type="button"
          className={`mail-rsvp-btn${status === "accepted" ? " is-active" : ""}`}
          disabled={busy}
          aria-pressed={status === "accepted"}
          onClick={() => void respond("accepted")}
        >
          {pending === "accepted" ? "Sending…" : "Accept"}
        </button>
        <button
          type="button"
          className={`mail-rsvp-btn${status === "declined" ? " is-active" : ""}`}
          disabled={busy}
          aria-pressed={status === "declined"}
          onClick={() => void respond("declined")}
        >
          {pending === "declined" ? "Sending…" : "Reject"}
        </button>
        <button
          type="button"
          className={`mail-rsvp-btn${status === "tentative" ? " is-active" : ""}`}
          disabled={busy}
          aria-pressed={status === "tentative"}
          onClick={() => void respond("tentative")}
        >
          {pending === "tentative" ? "Sending…" : "Tentative"}
        </button>
      </div>

      {logNewestFirst.length ? (
        <div className="mail-rsvp-log" aria-label="Sent responses">
          <p className="mail-rsvp-log-title">Sent responses</p>
          <ul className="mail-rsvp-log-list">
            {logNewestFirst.map((entry) => (
              <li key={`${entry.at}-${entry.responseStatus}`} className="mail-rsvp-log-item">
                <div className="mail-rsvp-log-meta">
                  <strong>{rsvpLabel(entry.responseStatus)}</strong>
                  <span className="muted">{formatLogWhen(entry.at)}</span>
                </div>
                {entry.comment ? (
                  <p className="mail-rsvp-log-comment">{entry.comment}</p>
                ) : (
                  <p className="mail-rsvp-log-comment muted">No message</p>
                )}
              </li>
            ))}
          </ul>
          <p className="mail-rsvp-log-foot muted">
            Sent from{" "}
            <a href={HUB_DOWNLOAD_URL} target="_blank" rel="noreferrer">
              ATW Hub
            </a>
          </p>
        </div>
      ) : null}

      {error ? (
        <p className="mail-rsvp-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
